<template>
	<div class="music-player">
		<div v-for="(track, index) in tracks" :key="track.id" class="music-track" @click="openModal(index)">
			<button class="play-button">
				<img class="play-cover" :src="track.image_url || coverFallback" alt="" @error="onCoverError" />
				<svg class="play-icon" viewBox="0 0 18 18">
					<path
						d="M3.75 3.75044C3.74992 3.48651 3.81948 3.22723 3.95166 2.99879C4.08384 2.77034 4.27396 2.58081 4.50281 2.44934C4.73167 2.31787 4.99116 2.24911 5.25509 2.25001C5.51902 2.2509 5.77804 2.32142 6.006 2.45444L15.0037 7.70294C15.2308 7.8347 15.4193 8.02375 15.5505 8.2512C15.6816 8.47864 15.7507 8.73652 15.7509 8.99905C15.7512 9.26158 15.6825 9.51957 15.5518 9.74724C15.421 9.97492 15.2328 10.1643 15.006 10.2964L6.006 15.5464C5.77804 15.6795 5.51902 15.75 5.25509 15.7509C4.99116 15.7518 4.73167 15.683 4.50281 15.5515C4.27396 15.4201 4.08384 15.2305 3.95166 15.0021C3.81948 14.7737 3.74992 14.5144 3.75 14.2504V3.75044Z"
					/>
				</svg>
			</button>
			<div class="music-track-details">
				<p class="music-track-title">{{ track.title }} {{ index + 1 }}</p>
				<p class="music-track-duration">{{ formatDuration(track.duration) }}</p>
			</div>
			<button class="download-button" @click.stop="downloadTrack(track, index)">
				<img :src="downloadIcon" alt="Скачать" />
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { MusicTrackObject } from '@/types'
import downloadIcon from '@/resources/icons/music/download.svg'
import coverFallback from '@/resources/images/music/cover-fallback.png'

defineProps<{
	tracks: MusicTrackObject[]
}>()

const emit = defineEmits<{
	openModal: [index: number]
}>()

function onCoverError(e: Event) {
	const img = e.target as HTMLImageElement
	if (!img.src.endsWith(coverFallback)) img.src = coverFallback
}

function openModal(index: number) {
	emit('openModal', index)
}

async function downloadTrack(track: MusicTrackObject, index: number) {
	try {
		const response = await fetch(track.url)
		const blob = await response.blob()
		const blobUrl = URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = blobUrl
		link.download = `${track.title || 'track'} ${index + 1}.mp3`
		link.click()
		URL.revokeObjectURL(blobUrl)
	} catch (error) {
		console.error('Ошибка при скачивании трека:', error)
	}
}

function formatDuration(seconds: number): string {
	const mins = Math.floor(seconds / 60)
	const secs = Math.floor(seconds % 60)
	return `${mins}:${secs.toString().padStart(2, '0')}`
}
</script>

<style lang="scss" scoped>
.music-player {
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-top: 12px;
	max-width: 320px;
}

.music-track {
	position: relative;
	display: flex;
	align-items: center;
	gap: 8px;
	background: var(--background-track);
	border-radius: var(--radius-md);
	padding: 6px 44px 6px 6px;
	width: fit-content;
	cursor: pointer;
}

.play-button {
	position: relative;
	overflow: hidden;
	width: 44px;
	height: 44px;
	border-radius: 50%;
	background: var(--action-primary);
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.play-cover {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.play-icon {
	position: relative;
	z-index: 1;
	width: 18px;
	height: 18px;
	fill: white;
	filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
}

.download-button {
	position: absolute;
	right: 12px;
	bottom: 8px;
	padding: 0;
	background: none;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: opacity 0.2s;

	img {
		width: 21px;
		height: 21px;
	}

	@media (hover: hover) {
		&:hover {
			opacity: 0.7;
		}
	}
}

.music-track-details {
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 120px;
}

.music-track-title {
	font: var(--font-label);
	color: var(--text-primary);
	margin: 0;
}

.music-track-duration {
	font: var(--font-caption);
	color: var(--text-secondary);
	margin: 0;
}
</style>
